import {
  Box,
  Typography,
  Button,
  Menu,
  MenuItem,
  TextField,
  Stack,
  useMediaQuery,
  useTheme,
  Chip,
} from "@mui/material";
import { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import {
  CalendarToday,
  Today,
  EventNote,
  DateRange,
  ExpandMore,
  AccessTime,
} from "@mui/icons-material";
import { format } from "date-fns";
import { useLocation } from "react-router-dom";
import PageURL from "../../constants/PageURL";
import { useSelector } from "react-redux";

const SubHeader = ({
  open,
  selectedValue,
  selectedLebel,
  sestSelectedLebel,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { projectDetail } = useSelector((state) => state.projectDetail);

  const [anchorEl, setAnchorEl] = useState(null);
  const [activeDateSelection, setActiveDateSelection] = useState(null);
  const location = useLocation();

  const intervalValue = projectDetail?.plantDataAvailability?.timeInterval;
  const dayData = projectDetail?.plantDataAvailability?.dayData;

  useEffect(() => {
    if (selectedLebel.startDate && selectedLebel.endDate) {
      const start = new Date(selectedLebel.startDate);
      start.setHours(0, 0, 0, 0); // Start of day (00:00:00)

      let end = new Date(selectedLebel.endDate);

      // Handle month-wise range
      if (selectedLebel.dateRangeType === "month") {
        end = new Date(end.getFullYear(), end.getMonth() + 1, 0); // Last day of month
      }
      // Handle year-wise range
      else if (selectedLebel.dateRangeType === "year") {
        end = new Date(end.getFullYear() + 1, 0, 0); // Last day of year
      }

      end.setHours(23, 59, 59, 999); // End of day (23:59:59.999)

      const startTimestamp = Math.floor(start.getTime() / 1000);
      const endTimestamp = Math.floor(end.getTime() / 1000);

      sestSelectedLebel({
        ...selectedLebel,
        startTimestamp,
        endTimestamp,
        isReady: true, // Mark as ready
      });
    }
  }, [selectedLebel.endDate, selectedLebel.dateRangeType]); // Add dateRangeType to dependencies

  useEffect(() => {
    const isSpecialPage = [
      PageURL.INDIA_INVERTER_DEVIATION,
      PageURL.INDIA_INVERTER_EFFICIENCY,
      PageURL.SCBSMB_LOSS,
      PageURL.INDIA_INVERTER_HEATMAP_DIAGRAM,
    ].includes(location.pathname);
    sestSelectedLebel({
      ...selectedLebel,
      startDate: isSpecialPage && intervalValue ? new Date() : null,
      endDate: isSpecialPage && intervalValue ? new Date() : null,
      dateRangeType: intervalValue && isSpecialPage ? intervalValue : "month",
      startTimestamp: null,
      endTimestamp: null,
      isReady: true,
    });
  }, [location.pathname]);

  const handleRangeTypeChange = (type) => {
    if (selectedLebel?.dateRangeType === type) {
      setAnchorEl(null);
      setActiveDateSelection(null);
      return;
    }
    sestSelectedLebel({
      ...selectedLebel,
      dateRangeType: type,
      startDate: type === intervalValue ? new Date() : null,
      endDate: type === intervalValue ? new Date() : null,
      startTimestamp: null,
      endTimestamp: null,
    });
    setAnchorEl(null);
    setActiveDateSelection(null);
  };

  const handleDateChange = (dates) => {
    // const [startDate, endDate] = dates;
    const startDate = dates?.[0] || dates;
    const endDate = dates?.[1];
    if (!startDate && !endDate) {
      setActiveDateSelection(null);
      return;
    }

    if (startDate && isNaN(new Date(startDate)?.getTime())) return;
    if (endDate && isNaN(new Date(endDate)?.getTime())) return;

    // Handle different range types
    switch (selectedLebel?.dateRangeType) {
      case intervalValue:
        // For day selection with interval, use single date
        const dayDate = startDate || selectedLebel.startDate;
        sestSelectedLebel({
          ...selectedLebel,
          startDate: dayDate,
          endDate: dayDate,
        });
        break;
      case "day":
        // Two-step selection for custom range
        if (!activeDateSelection && startDate) {
          // First selection - set startDate date
          setActiveDateSelection(startDate);
          sestSelectedLebel({ ...selectedLebel, startDate, endDate: null });
        } else if (activeDateSelection && endDate) {
          // Second selection - complete the range
          const sortedStart = startDate < endDate ? startDate : endDate;
          const sortedEnd = startDate < endDate ? endDate : startDate;
          sestSelectedLebel({
            ...selectedLebel,
            startDate: sortedStart,
            endDate: sortedEnd,
          });

          setActiveDateSelection(null);
        } else if (activeDateSelection && startDate) {
          sestSelectedLebel({
            ...selectedLebel,
            startDate,
          });
          setActiveDateSelection(startDate);
        }
        break;

      case "month":
        if (!activeDateSelection && startDate) {
          // First selection - set startDate date
          setActiveDateSelection(startDate);
          sestSelectedLebel({ ...selectedLebel, startDate, endDate: null });
        } else if (activeDateSelection && endDate) {
          // Second selection - complete the range
          const sortedStart = startDate < endDate ? startDate : endDate;
          const sortedEnd = startDate < endDate ? endDate : startDate;
          sestSelectedLebel({
            ...selectedLebel,
            startDate: sortedStart,
            endDate: sortedEnd,
          });

          setActiveDateSelection(null);
        } else if (activeDateSelection && startDate) {
          sestSelectedLebel({
            ...selectedLebel,
            startDate,
          });
          setActiveDateSelection(startDate);
        }

        break;

      case "year":
        if (!activeDateSelection && startDate) {
          // First selection - set startDate date
          setActiveDateSelection(startDate);
          sestSelectedLebel({ ...selectedLebel, startDate, endDate: null });
        } else if (activeDateSelection && endDate) {
          // Second selection - complete the range
          const sortedStart = startDate < endDate ? startDate : endDate;
          const sortedEnd = startDate < endDate ? endDate : startDate;
          sestSelectedLebel({
            ...selectedLebel,
            startDate: sortedStart,
            endDate: sortedEnd,
          });

          setActiveDateSelection(null);
        } else if (activeDateSelection && startDate) {
          sestSelectedLebel({
            ...selectedLebel,
            startDate,
          });
          setActiveDateSelection(startDate);
        }

        break;

      case "custom":
        // Two-step selection for custom range
        if (!activeDateSelection && startDate) {
          // First selection - set startDate date
          setActiveDateSelection(startDate);
          sestSelectedLebel({ ...selectedLebel, startDate, endDate: null });
        } else if (activeDateSelection && endDate) {
          // Second selection - complete the range
          const sortedStart = startDate < endDate ? startDate : endDate;
          const sortedEnd = startDate < endDate ? endDate : startDate;
          sestSelectedLebel({
            ...selectedLebel,
            startDate: sortedStart,
            endDate: sortedEnd,
          });
          setActiveDateSelection(null);
        } else if (activeDateSelection && startDate) {
          sestSelectedLebel({
            ...selectedLebel,
            startDate,
          });
          setActiveDateSelection(startDate);
        }
        break;

      default:
        break;
    }
  };

  const formatDisplayDate = () => {
    if (!selectedLebel.startDate) return "Select date range";
    switch (selectedLebel) {
      case intervalValue:
        return `${format(
          selectedLebel.startDate,
          "MMMM d, yyyy"
        )} (5-min intervals)`;
      case "day":
        // For day mode without interval, show range in yyyy-mm-dd format
        return selectedLebel?.timeInterval === null
          ? `${format(selectedLebel.startDate, "yyyy-MM-dd")} to ${format(
              selectedLebel.endDate,
              "yyyy-MM-dd"
            )}`
          : format(selectedLebel.startDate, "yyyy-MM-dd");
      case "month":
        return selectedLebel.endDate
          ? `${format(selectedLebel.startDate, "MM yyyy")} - ${format(
              selectedLebel.endDate,
              "MM yyyy"
            )}`
          : `Select endDate date (startDate: ${format(
              selectedLebel.startDate,
              "MM yyyy"
            )})`;

      case "year":
        return selectedLebel.endDate
          ? `${format(selectedLebel.startDate, "yyyy")} - ${format(
              selectedLebel.endDate,
              "yyyy"
            )}`
          : `Select endDate date (startDate: ${format(
              selectedLebel.startDate,
              "yyyy"
            )})`;

      case "custom":
        return selectedLebel.endDate
          ? `${format(selectedLebel.startDate, "MMM d, yyyy")} - ${format(
              selectedLebel.endDate,
              "MMM d, yyyy"
            )}`
          : `Select endDate date (startDate: ${format(
              selectedLebel.startDate,
              "MMM d, yyyy"
            )})`;
      default:
        return "Select date range";
    }
  };

  // Configure DatePicker props based on range type
  const getDatePickerProps = () => {
    const baseProps = {
      selected: selectedLebel.startDate,
      onChange: handleDateChange,
      customInput: (
        <TextField
          size="small"
          value={formatDisplayDate()}
          sx={{ width: "100%" }}
          
          InputProps={{
            readOnly: true,
            startAdornment:
              selectedLebel?.dateRangeType === intervalValue ? (
                <AccessTime />
              ) : selectedLebel?.dateRangeType === "day" ? (
                <Today color="action" sx={{ mr: 1 }} />
              ) : selectedLebel?.dateRangeType === "month" ? (
                <EventNote color="action" sx={{ mr: 1 }} />
              ) : selectedLebel?.dateRangeType === "year" ? (
                <DateRange color="action" sx={{ mr: 1 }} />
              ) : (
                <CalendarToday color="action" sx={{ mr: 1 }} />
              ),
          }}
        />
      ),
      // shouldCloseOnSelect: selectedLebel !== "custom",
      onCalendarOpen: () => setActiveDateSelection(null),
      inline: false,
      isClearable: false,
      // withPortal: true,  // Optional: forces calendar to appear in portal
    // disabledKeyboardNavigation: true,  // Disables keyboard suggestions
    };

    switch (selectedLebel?.dateRangeType) {
      case intervalValue:
        return {
          ...baseProps,
          selectsRange: false,
          dateFormat: "yyyy-MM-dd",
        };
      case "day":
        return {
          ...baseProps,
          selectsRange: true,
          startDate: selectedLebel.startDate,
          endDate: selectedLebel.endDate,
          selectsStart: true,
          selectsEnd: true,
          dateFormat: "yyyy-MM-dd",
        };

      case "month":
        return {
          ...baseProps,
          selectsRange: true,
          startDate: selectedLebel.startDate,
          endDate: selectedLebel.endDate,
          dateFormat: "yyyy-MM",
          selectsStart: true,
          selectsEnd: true,
          showMonthYearPicker: true,
        };
      case "year":
        return {
          ...baseProps,
          selectsRange: true,
          startDate: selectedLebel.startDate,
          endDate: selectedLebel.endDate,
          dateFormat: "yyyy",
          selectsStart: true,
          selectsEnd: true,
          showYearPicker: true,
        };
      case "custom":
        return {
          ...baseProps,
          selectsRange: true,
          startDate: selectedLebel.startDate,
          endDate: selectedLebel.endDate,
          selectsStart: true,
          selectsEnd: true,
          dateFormat: "yyyy-MM-dd",
        };
      default:
        return baseProps;
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "10px 25px",
        backgroundColor: "#f5f7fa",
        borderBottom: "1px solid #e0e0e0",
        position: "sticky",
        top: 0,
        zIndex: 1100,
        flexDirection: "row",
        gap: isMobile ? 2 : 0,
      }}
    >
      {/* Left side - Project details */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          marginLeft: !open && !isMobile ? "2rem" : 0,
          flexWrap: "wrap",
          rowGap: 1,
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
          {selectedValue.country || "Country"}
        </Typography>
        <ArrowForwardIosIcon sx={{ fontSize: 14, mx: 1 }} />
        <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
          {selectedValue.partnerName || "Partner Name"}
        </Typography>
        <ArrowForwardIosIcon sx={{ fontSize: 14, mx: 1 }} />
        <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
          {selectedValue.projectName || "Project Name"}
        </Typography>
      </Box>

      {/* Right side - Date range selector */}

      {location.pathname !== PageURL.COUNTRYDEFAULT &&
        location.pathname !== PageURL.PROJECT_DETAILS &&
        location.pathname !== PageURL.INDIA_PROJ_OVERVIEW &&
        location.pathname !== PageURL.INDIA_LOSS_FLOW &&
        location.pathname !== PageURL.INDIA_PRES_MODEL_Fault_Tree &&
        location.pathname !== PageURL.INDIA_PRES_MODEL_Loss_Flow && (
          <Stack
            direction="row"
            alignItems="center"
            gap={1}
            sx={{ flexWrap: "wrap" }}
          >
            <Button
              variant="outlined"
              onClick={(e) => setAnchorEl(e.currentTarget)}
              startIcon={
                selectedLebel?.dateRangeType === intervalValue ? (
                  <AccessTime />
                ) : selectedLebel?.dateRangeType === "day" ? (
                  <Today />
                ) : selectedLebel?.dateRangeType === "month" ? (
                  <EventNote />
                ) : selectedLebel?.dateRangeType === "year" ? (
                  <DateRange />
                ) : (
                  <CalendarToday />
                )
              }
              endIcon={<ExpandMore />}
              sx={{
                textTransform: "capitalize",
                minWidth: 100,
              }}
            >
              {selectedLebel?.dateRangeType}
            </Button>

            {/* {selectedLebel?.dateRangeType === "day" &&
              projectDetail?.plantDataAvailability?.timeInterval && (
                <Button
                  variant={
                    selectedLebel.timeInterval ? "contained" : "outlined"
                  }
                  onClick={(e) => setIntervalAnchorEl(e.currentTarget)}
                  startIcon={<AccessTime />}
                  sx={{
                    textTransform: "capitalize",
                    minWidth: 100,
                  }}
                >
                  {selectedLebel.timeInterval ? "5-min" : "Interval"}
                </Button>
              )} */}

            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={() => setAnchorEl(null)}
            >
              {intervalValue &&
                (location.pathname === PageURL.INDIA_INVERTER_DEVIATION ||
                  location.pathname === PageURL.INDIA_INVERTER_EFFICIENCY ||
                  location.pathname === PageURL.SCBSMB_LOSS ||
                  location.pathname ===
                    PageURL.INDIA_INVERTER_HEATMAP_DIAGRAM) && (
                  <MenuItem
                    onClick={() => handleRangeTypeChange(intervalValue)}
                  >
                    <AccessTime sx={{ mr: 1 }} /> {intervalValue} Interval
                  </MenuItem>
                )}
              {projectDetail?.plantDataAvailability?.dayData && (
                <MenuItem onClick={() => handleRangeTypeChange("day")}>
                  <Today sx={{ mr: 1 }} /> Day
                </MenuItem>
              )}

              <MenuItem onClick={() => handleRangeTypeChange("month")}>
                <EventNote sx={{ mr: 1 }} /> Month
              </MenuItem>
              <MenuItem onClick={() => handleRangeTypeChange("year")}>
                <DateRange sx={{ mr: 1 }} /> Year
              </MenuItem>
              {/* <MenuItem onClick={() => handleRangeTypeChange("custom")}>
                <CalendarToday sx={{ mr: 1 }} /> Custom Range
              </MenuItem> */}
            </Menu>
            {/* {projectDetail?.plantDataAvailability?.timeInterval && (
              <Menu
                anchorEl={intervalAnchorEl}
                open={Boolean(intervalAnchorEl)}
                onClose={() => setIntervalAnchorEl(null)}
              >
                {projectDetail?.plantDataAvailability?.timeInterval && (
                  <MenuItem
                    onClick={() =>
                      handleTimeIntervalChange(
                        projectDetail?.plantDataAvailability?.timeInterval
                      )
                    }
                  >
                  
                  </MenuItem>
                )}
                <MenuItem onClick={() => handleTimeIntervalChange(null)}>
                  No Interval
                </MenuItem>
              </Menu>
            )} */}

            <Box sx={{ minWidth: isMobile ? "100%" : 220 }}>
              <DatePicker
                {...getDatePickerProps()}
                maxDate={
                  selectedLebel?.dateRangeType === "day" && activeDateSelection
                    ? new Date(
                        new Date(
                          activeDateSelection.getFullYear(),
                          activeDateSelection.getMonth() + 1,
                          0
                        )
                      )
                    : undefined
                }
                
              />
            </Box>
          </Stack>
        )}
    </Box>
  );
};

export default SubHeader;
